import { flatten } from "lodash";
import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { ChatRoomParticipant } from "@circle-react/types/Chat/ChatRoomParticipant";
import { PARTICIPANTS_DEFAULT_PER_PAGE } from "./useChatParticipantsApi";

interface UpdateMissingParticipantsProps {
  enabled: boolean;
  ids: number[];
  shouldUpdateCache?: boolean;
  uuid: string;
}

export const useUpdateMissingParticipants = ({
  uuid: chatRoomUuid,
  ids,
  enabled,
  shouldUpdateCache = true,
}: UpdateMissingParticipantsProps) => {
  const queryClient = useQueryClient();

  const updateQueryCache = (newParticipants: ChatRoomParticipant[]) => {
    queryClient.setQueryData(
      ["chat-participants", chatRoomUuid],
      (oldData: any = { pages: [] }) => {
        const oldParticipants = flatten(
          (oldData.pages || []).map((page: any) => page.records),
        );
        const allParticipants = [...oldParticipants, ...newParticipants];
        const pagesCount = Math.ceil(
          allParticipants.length / PARTICIPANTS_DEFAULT_PER_PAGE,
        );
        if (pagesCount > oldData.pages.length) {
          oldData.pages = Array.from({ length: pagesCount }).fill(
            oldData.pages[pagesCount] ||
              oldData.pages[oldData.pages.length - 1],
          );
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page: any, index: number) => ({
            ...(page || {}),
            records: allParticipants.slice(
              PARTICIPANTS_DEFAULT_PER_PAGE * index,
              PARTICIPANTS_DEFAULT_PER_PAGE * (index + 1),
            ),
          })),
        };
      },
    );
  };

  const { isLoading, data: participants = [] } = useQuery(
    ["chatroom", "missing-participants", ids],
    () =>
      reactQueryGet(
        internalApi.chatRoomParticipants.index({
          uuid: chatRoomUuid,
          params: { ids },
        }),
      ),
    {
      enabled,
      select: ({ records }) => records,
      onSuccess: missingParticipants => {
        shouldUpdateCache && updateQueryCache(missingParticipants);
      },
      onError: err => {
        console.error("err:", err);
      },
    },
  );

  return { isLoading, participants };
};
